import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import QrScanner from 'qr-scanner';
import Card from '../../atoms/Card';
import Button from '../../atoms/Button';
import Icon from '../../atoms/Icon/Icon';
import {Input} from '../../atoms/Input';
import './EventQRScanner.scss';

interface EventQRScannerProps {
  onEventScanned: (eventId: number) => void;
  className?: string;
}

const EventQRScanner: React.FC<EventQRScannerProps> = ({ onEventScanned, className = '' }) => {
  const { t } = useTranslation();
  const [isScanning, setIsScanning] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manualEventId, setManualEventId] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);

  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.destroy();
      }
    };
  }, []);

  const startScanning = () => {
    setError(null);
    setIsScanning(true);
  };

  // Effect to start the actual scanner when isScanning becomes true
  useEffect(() => {
    if (isScanning && videoRef.current) {
      const initializeScanner = async () => {
        try {
          // Check if QrScanner is available
          if (typeof QrScanner === 'undefined') {
            console.error('QrScanner library not loaded');
            setError(t('eventQRScanner.scannerError'));
            setIsScanning(false);
            return;
          }

          const qrScanner = new QrScanner(
            videoRef.current!,
            (result) => {
              console.log('QR Code detected:', result.data);
              try {
                // Try to parse the result as an event ID
                const eventId = parseInt(result.data);
                if (!isNaN(eventId) && eventId > 0) {
                  onEventScanned(eventId);
                  stopScanning();
                } else {
                  console.log('Invalid event ID:', result.data);
                  setError(t('eventQRScanner.invalidEventCode'));
                }
              } catch (err) {
                console.error('Error parsing QR result:', err);
                setError(t('eventQRScanner.invalidEventCode'));
              }
            },
            {
              onDecodeError: (err) => {
                const errorMessage = err?.toString() || '';
                
                // Ignore "No QR code found" errors - these are normal during scanning
                if (errorMessage.includes('No QR code found') || 
                    errorMessage.includes('QR code not found') ||
                    errorMessage.includes('No QR code detected')) {
                  return; // Don't log or handle these normal scanning errors
                }
                
                // Only handle critical camera access errors
                if (errorMessage.includes('Permission') || 
                    errorMessage.includes('NotAllowed') || 
                    errorMessage.includes('NotFound') ||
                    errorMessage.includes('camera') ||
                    errorMessage.includes('getUserMedia')) {
                  console.error('Camera access error:', err);
                  setError(t('eventQRScanner.cameraDenied'));
                  setIsScanning(false);
                }
              },
              preferredCamera: 'environment', // Use back camera
              highlightScanRegion: true,
              highlightCodeOutline: true,
            }
          );

          qrScannerRef.current = qrScanner;
          await qrScanner.start();
          setHasPermission(true);
        } catch (err: any) {
          console.error('Error starting QR scanner:', err);
          
          // Check if it's an HTTPS issue
          if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
            setError(t('eventQRScanner.httpsRequired'));
          } else if (err.name === 'NotAllowedError') {
            setError(t('eventQRScanner.cameraDenied'));
            setHasPermission(false);
          } else if (err.name === 'NotFoundError') {
            setError(t('eventQRScanner.noCamera'));
            setHasPermission(false);
          } else {
            setError(t('eventQRScanner.scannerError'));
          }
          setIsScanning(false);
        }
      };

      initializeScanner();
    }
  }, [isScanning, onEventScanned, t]);

  const stopScanning = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    setIsScanning(false);
  };

  const handleManualEntry = () => {
    setShowManualEntry(true);
  };

  const handleManualSubmit = () => {
    const id = parseInt(manualEventId);
    if (!isNaN(id) && id > 0) {
      onEventScanned(id);
    } else {
      setError(t('eventQRScanner.invalidEventId'));
    }
  };

  const handleManualCancel = () => {
    setShowManualEntry(false);
    setManualEventId('');
    setError(null);
  };

  return (
    <Card className={`event-qr-scanner ${className}`}>
      <div className="event-qr-scanner__header">
        <h3 className="event-qr-scanner__title">
          <Icon name="qr" size={24} alt={t('eventQRScanner.title')} />
          {t('eventQRScanner.title')}
        </h3>
        <p className="event-qr-scanner__description">
          {t('eventQRScanner.description')}
        </p>
      </div>

      <div className="event-qr-scanner__content">
        {!isScanning && !showManualEntry ? (
          <div className="event-qr-scanner__start">
            <div className="event-qr-scanner__camera-placeholder">
              <Icon name="camara" size={64} alt={t('eventQRScanner.camera')} />
              <p>{t('eventQRScanner.pointCamera')}</p>
            </div>
            <div className="event-qr-scanner__actions">
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  startScanning();
                }}
                disabled={hasPermission === false}
              >
                <Icon name="qr" size={20} alt={t('eventQRScanner.scan')} />
                {t('eventQRScanner.scan')}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={handleManualEntry}
              >
                <Icon name="teclado" size={20} alt={t('eventQRScanner.manualEntry')} />
                {t('eventQRScanner.manualEntry')}
              </Button>
            </div>
          </div>
        ) : showManualEntry ? (
          <div className="event-qr-scanner__manual-entry">
            <div className="event-qr-scanner__manual-form">
              <div className="event-qr-scanner__input-group">
                <label className="event-qr-scanner__label">
                  {t('eventQRScanner.enterEventId')}
                </label>
                <Input
                  type="number"
                  value={manualEventId}
                  onChange={(e) => setManualEventId(e.target.value)}
                  placeholder="123"
                  required
                  size="lg"
                />
              </div>
              <div className="event-qr-scanner__manual-actions">
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleManualSubmit}
                  disabled={!manualEventId.trim()}
                >
                  <Icon name="eventos" size={20} alt={t('common.enter')} />
                  {t('common.enter')}
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  onClick={handleManualCancel}
                >
                  <Icon name="cerrar" size={20} alt={t('common.cancel')} />
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="event-qr-scanner__scanning">
            <div className="event-qr-scanner__camera-container">
              <video ref={videoRef} className="event-qr-scanner__camera" />
              
            </div>
            <div className="event-qr-scanner__scan-actions">
              <Button
                variant="outline"
                size="md"
                onClick={stopScanning}
              >
                <Icon name="cerrar" size={20} alt={t('eventQRScanner.stop')} />
                {t('eventQRScanner.stop')}
              </Button>
            </div>
          </div>
        )}

        {error && (
          <div className="event-qr-scanner__error">
            <Icon name="alerta" size={20} alt={t('common.error')} />
            <p>{error}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
            >
              {t('common.dismiss')}
            </Button>
          </div>
        )}

        {hasPermission === false && (
          <div className="event-qr-scanner__permission-error">
            <Icon name="alerta" size={20} alt={t('eventQRScanner.permissionRequired')} />
            <p>{t('eventQRScanner.permissionRequired')}</p>
            <p className="event-qr-scanner__permission-help">
              {t('eventQRScanner.permissionHelp')}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default EventQRScanner;
